import { useEffect, useState } from 'react';
import { Link, Outlet, useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { Store,
  ChevronDown,
  Crown,
  Loader2,
  LogIn,
  LogOut,
  MapPin,
  Navigation,
  Package,
  Receipt,
  Search,
  ShoppingBag,
  UserRound,
  X,
} from 'lucide-react';
import { CITY, UserRole } from '@kick/types';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth-store';
import { cantidadTotal, useCarrito } from '@/lib/carrito';
import { AvisoCerrado } from '@/components/AvisoCerrado';
import { Campana } from '@/components/Campana';
import { BotonSoporte } from '@/components/BotonSoporte';
import { useDirecciones, useMiPremium } from '@/lib/queries';
import { cerrarSocket } from '@/lib/socket';
import { useUbicacion } from '@/lib/ubicacion';
import { cn } from '@/lib/cn';
import { BarreraError } from './BarreraError';
import { PanelCarrito } from './PanelCarrito';
import { Boton, Modal } from './ui';
import { useAvisos } from './avisos';

export function Layout() {
  const [carritoAbierto, setCarritoAbierto] = useState(false);

  return (
    <div className="min-h-dvh bg-kick-light">
      <Encabezado onAbrirCarrito={() => setCarritoAbierto(true)} />

      <main className="mx-auto max-w-6xl px-4 py-6 sm:px-6">
        <BarreraError>
          <Outlet context={{ abrirCarrito: () => setCarritoAbierto(true) }} />
        </BarreraError>
      </main>

      <PanelCarrito abierto={carritoAbierto} onCerrar={() => setCarritoAbierto(false)} />
      <BarraInferior onAbrirCarrito={() => setCarritoAbierto(true)} />
      <BotonSoporte />
      <AvisoCerrado />
    </div>
  );
}

function Encabezado({ onAbrirCarrito }: { onAbrirCarrito: () => void }) {
  const navegar = useNavigate();
  const lineas = useCarrito((s) => s.lineas);
  const total = cantidadTotal(lineas);
  const [buscando, setBuscando] = useState('');

  return (
    <header className="sticky top-0 z-30 bg-kick-dark">
      <div className="mx-auto flex max-w-6xl items-center gap-3 px-4 py-3 sm:px-6">
        <Link to="/" aria-label="Kick Domicilios — inicio" className="shrink-0">
          <img src="/logo.png" alt="Kick Domicilios" className="h-10 w-auto" />
        </Link>

        <SelectorUbicacion />

        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (buscando.trim().length >= 2) {
              navegar(`/buscar?q=${encodeURIComponent(buscando.trim())}`);
            }
          }}
          className="relative ml-auto hidden max-w-xs flex-1 md:block"
        >
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-white/50" />
          <input
            type="search"
            value={buscando}
            onChange={(e) => setBuscando(e.target.value)}
            placeholder="Buscar comercios o platos"
            aria-label="Buscar"
            className="w-full rounded-full bg-white/10 py-2 pl-9 pr-3 text-sm text-white placeholder:text-white/50 outline-none transition focus:bg-white/15"
          />
        </form>

        <div className="ml-auto flex items-center gap-1 md:ml-0">
          <Link
            to="/buscar"
            aria-label="Buscar"
            className="rounded-kick p-2 text-white/80 transition hover:bg-white/10 md:hidden"
          >
            <Search className="h-5 w-5" />
          </Link>

          {/*
            Encomiendas, en la cabecera y no escondido en el menú de la cuenta.
            Es un servicio distinto de comprar en un comercio: quien entra a
            mandar un paquete no viene a mirar el catálogo, y si hay que
            buscarlo no se entera de que existe.
          */}
          <Link
            to="/encomiendas"
            /* En pantallas chicas solo queda el icono: sin la etiqueta, el
               enlace no tiene nombre para el lector de pantalla. */
            aria-label="Encomiendas"
            className="flex items-center gap-1.5 rounded-kick px-2 py-2 text-sm font-semibold text-white/80 transition hover:bg-white/10"
          >
            <Package className="h-5 w-5" />
            <span className="hidden lg:inline">Encomiendas</span>
          </Link>

          {/* Lo que no está en Kick también se puede pedir: alguien lo compra y lo lleva. */}
          <Link
            to="/no-aliados"
            aria-label="Comercios no aliados"
            className="flex items-center gap-1.5 rounded-kick px-2 py-2 text-sm font-semibold text-white/80 transition hover:bg-white/10"
          >
            <Store className="h-5 w-5" />
            <span className="hidden lg:inline">No aliados</span>
          </Link>

          <Campana />
          <MenuCuenta />

          <button
            onClick={onAbrirCarrito}
            aria-label={`Carrito, ${total} producto${total === 1 ? '' : 's'}`}
            className="relative hidden rounded-kick p-2 text-white/80 transition hover:bg-white/10 sm:block"
          >
            <ShoppingBag className="h-5 w-5" />
            {total > 0 && (
              <span className="absolute -right-0.5 -top-0.5 grid h-5 min-w-5 place-items-center rounded-full bg-kick-red px-1 text-[11px] font-bold text-white">
                {total}
              </span>
            )}
          </button>
        </div>
      </div>
    </header>
  );
}

/** Barra fija en móvil: el carrito debe estar siempre a un toque. */
function BarraInferior({ onAbrirCarrito }: { onAbrirCarrito: () => void }) {
  const lineas = useCarrito((s) => s.lineas);
  const total = cantidadTotal(lineas);

  if (total === 0) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-20 border-t border-kick-gray/15 bg-white p-3 sm:hidden">
      <Boton onClick={onAbrirCarrito} className="w-full" icono={<ShoppingBag className="h-4 w-4" />}>
        Ver mi pedido ({total})
      </Boton>
    </div>
  );
}

// ── Ubicación ───────────────────────────────────────────────

function SelectorUbicacion() {
  const { ubicacion, localizando, error, fijar, usarGps } = useUbicacion();
  const accessToken = useAuth((s) => s.accessToken);
  const direcciones = useDirecciones(!!accessToken);
  const [abierto, setAbierto] = useState(false);
  const avisos = useAvisos();

  useEffect(() => {
    if (error) avisos.error(error);
    // avisos cambia en cada render; solo interesa reaccionar al error.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error]);

  return (
    <>
      <button
        onClick={() => setAbierto(true)}
        className="flex min-w-0 items-center gap-1.5 rounded-kick px-2 py-1.5 text-left transition hover:bg-white/10"
      >
        <MapPin className="h-4 w-4 shrink-0 text-kick-red" />
        <span className="min-w-0">
          <span className="block text-[11px] leading-none text-white/50">Entregar en</span>
          <span className="block truncate text-sm font-semibold text-white">
            {ubicacion?.label ?? CITY.name}
          </span>
        </span>
        <ChevronDown className="h-4 w-4 shrink-0 text-white/50" />
      </button>

      <Modal
        abierto={abierto}
        onCerrar={() => setAbierto(false)}
        titulo="¿Dónde entregamos?"
        descripcion="Usamos tu ubicación para mostrarte los comercios que llegan hasta allá."
        ancho="sm"
      >
        <div className="space-y-2 p-5">
          <button
            onClick={async () => {
              await usarGps();
              setAbierto(false);
            }}
            disabled={localizando}
            className="flex w-full items-center gap-3 rounded-kick border border-kick-gray/20 p-3.5 text-left transition hover:border-kick-red hover:bg-kick-red/5 disabled:opacity-60"
          >
            {localizando ? (
              <Loader2 className="h-5 w-5 shrink-0 animate-spin text-kick-red" />
            ) : (
              <Navigation className="h-5 w-5 shrink-0 text-kick-red" />
            )}
            <span>
              <span className="block font-semibold text-kick-ink">Usar mi ubicación actual</span>
              <span className="block text-sm text-kick-gray">
                {localizando ? 'Buscando…' : 'El navegador pedirá permiso'}
              </span>
            </span>
          </button>

          {accessToken && direcciones.data && direcciones.data.length > 0 && (
            <>
              <p className="pt-2 text-xs font-semibold uppercase tracking-wide text-kick-gray">
                Mis direcciones
              </p>
              {direcciones.data.map((direccion) => (
                <button
                  key={direccion.id}
                  onClick={() => {
                    fijar({
                      addressId: direccion.id,
                      label: direccion.label,
                      addressLine: direccion.addressLine,
                      location: direccion.location,
                    });
                    setAbierto(false);
                  }}
                  className={cn(
                    'flex w-full items-start gap-3 rounded-kick border p-3.5 text-left transition',
                    ubicacion?.addressId === direccion.id
                      ? 'border-kick-red bg-kick-red/5'
                      : 'border-kick-gray/20 hover:border-kick-gray/40',
                  )}
                >
                  <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-kick-gray" />
                  <span className="min-w-0">
                    <span className="block font-semibold text-kick-ink">{direccion.label}</span>
                    <span className="block truncate text-sm text-kick-gray">
                      {direccion.addressLine}
                    </span>
                  </span>
                </button>
              ))}
            </>
          )}

          {!accessToken && (
            <p className="pt-2 text-center text-sm text-kick-gray">
              <Link to="/acceso" className="font-semibold text-kick-red hover:underline">
                Inicia sesión
              </Link>{' '}
              para guardar tus direcciones.
            </p>
          )}
        </div>
      </Modal>
    </>
  );
}

// ── Cuenta ──────────────────────────────────────────────────

function MenuCuenta() {
  const { user, accessToken, cerrar } = useAuth();
  const navegar = useNavigate();
  const [abierto, setAbierto] = useState(false);
  const vaciarCarrito = useCarrito((s) => s.vaciar);
  // Solo con sesión de cliente: sin cuenta no hay insignia que consultar.
  const premium = useMiPremium(!!accessToken && user?.role === UserRole.CUSTOMER);

  const salir = useMutation({
    mutationFn: async () => {
      await api.post('/auth/logout', {});
    },
    onSettled: () => {
      cerrar();
      // El carrito es de quien lo armó: no debe quedar para el siguiente.
      vaciarCarrito();
      // El canal en vivo lleva el token de quien salía: se cierra con él.
      cerrarSocket();
      setAbierto(false);
      navegar('/');
    },
  });

  if (!accessToken) {
    return (
      <Link to="/acceso">
        {/* aria-label explícito: en móvil el texto se oculta y el botón
            quedaría sin nombre para un lector de pantalla. */}
        <Boton
          aria-label="Ingresar a mi cuenta"
          className="px-3 py-2 text-sm"
          icono={<LogIn className="h-4 w-4" />}
        >
          <span className="hidden sm:inline">Ingresar</span>
        </Boton>
      </Link>
    );
  }

  return (
    <>
      <button
        onClick={() => setAbierto(true)}
        aria-label="Mi cuenta"
        className="flex items-center gap-2 rounded-kick p-2 text-white/80 transition hover:bg-white/10"
      >
        <UserRound className="h-5 w-5" />
        <span className="hidden text-sm lg:block">{user?.username}</span>
      </button>

      <Modal abierto={abierto} onCerrar={() => setAbierto(false)} titulo="Mi cuenta" ancho="sm">
        <div className="p-5">
          <p className="flex items-center gap-2 font-semibold text-kick-ink">
            {user?.fullName}
            {premium.data?.premium && (
              <span className="flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-bold text-amber-800">
                <Crown className="h-3 w-3" />
                Premium
              </span>
            )}
          </p>
          <p className="font-mono text-sm text-kick-gray">{user?.username}</p>

          {/*
            La insignia con su cuenta, no como misterio.
            Quien ya la tiene ve qué le da; quien no, ve cuánto le falta: es lo
            que convierte la insignia en un motivo para pedir el siguiente.
          */}
          {premium.data && (
            <p className="mt-2 rounded-kick bg-kick-light px-3 py-2 text-xs text-kick-gray">
              {premium.data.premium
                ? `Tienes ${premium.data.discountPercent}% de descuento automático en cada pedido.`
                : `Te ${premium.data.remaining === 1 ? 'falta' : 'faltan'} ${premium.data.remaining} ${premium.data.remaining === 1 ? 'pedido' : 'pedidos'} este mes para ser premium y ganar descuento en todo.`}
            </p>
          )}

          <div className="mt-5 space-y-2">
            <Link to="/pedidos" onClick={() => setAbierto(false)}>
              <Boton variante="secundario" className="w-full justify-start">
                <Receipt className="h-4 w-4" />
                Mis pedidos
              </Boton>
            </Link>

            <Link to="/mis-direcciones" onClick={() => setAbierto(false)}>
              <Boton variante="secundario" className="w-full justify-start">
                <MapPin className="h-4 w-4" />
                Mis direcciones
              </Boton>
            </Link>

            <Boton
              variante="secundario"
              className="w-full justify-start"
              cargando={salir.isPending}
              onClick={() => salir.mutate()}
            >
              <LogOut className="h-4 w-4" />
              Cerrar sesión
            </Boton>
          </div>
        </div>
      </Modal>
    </>
  );
}

export { X as IconoCerrar };
